import { useTranslation } from 'react-i18next';

import { Checkbox, Input, Label, SectionTitle } from './FormComponents';

import type { Consents } from '../../types/application';

export type StepFErrors = Partial<Record<keyof Consents, string>>;

interface StepFConsentsProps {
  value: Consents;
  fullName: string; // From stepA for signature validation
  errors: StepFErrors;
  onChange: (patch: Partial<Consents>) => void;
}

export function StepFConsents({ value, fullName, errors, onChange }: StepFConsentsProps) {
  const { t } = useTranslation();
  const today = new Date().toISOString().split('T')[0] ?? '';

  return (
    <div className='space-y-6'>
      <SectionTitle title={t('wizard.stepF.title')} subtitle={t('wizard.stepF.subtitle')} />

      {/* Legal Disclaimer */}
      <div className='bg-gray-50 p-4 rounded text-sm text-gray-700 leading-relaxed border'>
        <p>{t('wizard.stepF.legalDisclaimer1')}</p>
        <p className='mt-2'>{t('wizard.stepF.legalDisclaimer2')}</p>
      </div>

      {/* Consent Checkboxes */}
      <div className='space-y-4'>
        {/* Privacy & Data Use Consent */}
        <div
          className={`p-4 rounded-md border ${errors.consentPrivacy ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}>
          <Checkbox
            label={t('wizard.stepF.consentPrivacy')}
            checked={value.consentPrivacy}
            onChange={(checked) => onChange({ consentPrivacy: checked })}
            error={errors.consentPrivacy}
          />
          {errors.consentPrivacy ? <p className='text-xs text-red-600 mt-2 ml-6'>{errors.consentPrivacy}</p> : null}
        </div>

        {/* Messaging Consent */}
        <div
          className={`p-4 rounded-md border ${errors.consentMessaging ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}>
          <Checkbox
            label={t('wizard.stepF.consentMessaging')}
            checked={value.consentMessaging}
            onChange={(checked) => onChange({ consentMessaging: checked })}
            error={errors.consentMessaging}
          />
          {errors.consentMessaging ? <p className='text-xs text-red-600 mt-2 ml-6'>{errors.consentMessaging}</p> : null}
        </div>

        {/* Video Recording Consent */}
        <div
          className={`p-4 rounded-md border ${errors.consentVideoRecording ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}>
          <Checkbox
            label={t('wizard.stepF.consentVideoRecording')}
            checked={value.consentVideoRecording}
            onChange={(checked) => onChange({ consentVideoRecording: checked })}
            error={errors.consentVideoRecording}
          />
          <p className='text-xs text-gray-500 mt-1 ml-6'>{t('wizard.stepF.consentVideoHint')}</p>
          {errors.consentVideoRecording ? (
            <p className='text-xs text-red-600 mt-2 ml-6'>{errors.consentVideoRecording}</p>
          ) : null}
        </div>
      </div>

      {/* Electronic Signature */}
      <div className='border-t pt-6 mt-6'>
        <h3 className='font-semibold text-gray-900 mb-2'>{t('wizard.stepF.electronicSignature')}</h3>
        <p className='text-sm text-gray-600 mb-4'>{t('wizard.stepF.signatureInstructions')}</p>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div>
            <Label required error={errors.signature}>
              {t('wizard.stepF.signatureLabel')}
            </Label>
            <Input
              value={value.signature}
              onChange={(e) => onChange({ signature: e.target.value })}
              placeholder={t('wizard.stepF.signaturePlaceholder')}
              error={errors.signature}
              className='font-serif italic'
            />
            {fullName && value.signature && value.signature.toLowerCase() !== fullName.toLowerCase() ? (
              <p className='text-xs text-amber-600 mt-1'>
                {t('wizard.stepF.signatureMismatchWarning', { expected: fullName })}
              </p>
            ) : null}
          </div>

          <div>
            <Label required error={errors.signatureDate}>
              {t('wizard.stepF.signatureDate')}
            </Label>
            <Input
              type='date'
              value={value.signatureDate}
              onChange={(e) => onChange({ signatureDate: e.target.value })}
              max={today}
              error={errors.signatureDate}
            />
          </div>
        </div>

        <p className='text-xs text-gray-500 mt-4'>{t('wizard.stepF.signatureLegalNote')}</p>
      </div>
    </div>
  );
}
